import React, { useMemo, useState } from "react";
import { Box, Typography, Dialog, IconButton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useGetArchivedSubmissions } from "../api/reactQueryHook/useFormAssignments";
import { ArchivedSubmission_Type } from "../api/axiosNetworkRequest/formAssignments";
import MagicLoader from "./MagicLoader";

interface ArchiveViewProps {
  selectedCompanyId: number;
}

type ArchiveTab = "users" | "files";

// A group of archived submissions keyed by user (email) or by file.
interface ArchiveGroup {
  key: string;        // email_id (users) or file_name (files)
  label: string;      // primary line on the card
  rows: ArchivedSubmission_Type[];
}

const formatSubmittedAt = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

// Initials from an email local-part (e.g. "m.webb@x.com" -> "MW"); no display name is available.
const initialsFromEmail = (email: string): string => {
  const local = (email.split("@")[0] ?? "").trim();
  if (!local) return "?";
  const tokens = local.split(/[._\-+]+/).filter(Boolean);
  if (tokens.length >= 2) return (tokens[0][0] + tokens[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
};

// Derive a filename for the preview title from the presigned URL, falling back to the file name.
const previewName = (row: ArchivedSubmission_Type): string => {
  if (row.file_name) return row.file_name;
  try {
    const path = row.submission_url.split("?")[0];
    return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1)) || "Submission";
  } catch {
    return "Submission";
  }
};

// Drop a trailing .bpr for display (the domain's SOP file extension).
const cleanFileName = (name: string): string => name.replace(/\.bpr$/i, "");

const groupBy = (rows: ArchivedSubmission_Type[], keyFn: (r: ArchivedSubmission_Type) => string): ArchiveGroup[] => {
  const map = new Map<string, ArchivedSubmission_Type[]>();
  rows.forEach(r => {
    const k = keyFn(r);
    const list = map.get(k);
    if (list) list.push(r); else map.set(k, [r]);
  });
  return [...map.entries()].map(([key, groupRows]) => ({ key, label: key, rows: groupRows }));
};

const ArchiveView: React.FC<ArchiveViewProps> = ({ selectedCompanyId }) => {
  const theme = useTheme();
  const p = theme.palette;
  const [tab, setTab] = useState<ArchiveTab>("users");
  const [openGroup, setOpenGroup] = useState<ArchiveGroup | null>(null);
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);

  const { data, isFetching } = useGetArchivedSubmissions(selectedCompanyId);
  const rows = useMemo(() => data?.data ?? [], [data]);

  // Users tab = rows where the user was deleted (grouped by email); Files tab = file_deleted (grouped by file).
  const userGroups = useMemo(() => groupBy(rows.filter(r => r.user_deleted), r => r.email_id), [rows]);
  const fileGroups = useMemo(() => groupBy(rows.filter(r => r.file_deleted), r => r.file_name), [rows]);

  const groups = tab === "users" ? userGroups : fileGroups;
  const countLabel = tab === "users" ? `${userGroups.length} archived users` : `${fileGroups.length} archived files`;

  const openPreview = (row: ArchivedSubmission_Type) => {
    if (row.submission_url) setPreview({ url: row.submission_url, name: previewName(row) });
  };

  const tabSx = (active: boolean) => ({
    fontWeight: 700, fontSize: "1.35rem", cursor: "pointer",
    color: active ? "text.primary" : "text.disabled",
    "&:hover": { color: active ? "text.primary" : "text.secondary" },
    transition: "color 0.15s",
  });

  return (
    <Box sx={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header: tabs + count */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Typography sx={tabSx(tab === "users")} onClick={() => setTab("users")}>Archived Users</Typography>
          <Typography sx={tabSx(tab === "files")} onClick={() => setTab("files")}>Archived Files</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
          {tab === "users"
            ? <PeopleAltOutlinedIcon sx={{ fontSize: "1.1rem" }} />
            : <InsertDriveFileOutlinedIcon sx={{ fontSize: "1.1rem" }} />}
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>{countLabel}</Typography>
        </Box>
      </Box>

      <MagicLoader isLoading={isFetching} variant="inline" />

      {/* Card grid */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 4, pb: 5 }}>
        {isFetching ? null : groups.length === 0 ? (
          <Typography sx={{ color: "text.secondary", textAlign: "center", py: 8, fontSize: "0.9rem" }}>
            {tab === "users" ? "No archived users." : "No archived files."}
          </Typography>
        ) : (
          // Locked 3-column poster grid (matches the mockup); minmax(0,1fr) lets cells shrink below content width.
          <Box sx={{ maxWidth: 860, mx: "auto", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1.75 }}>
            {groups.map(g => {
              const isUsers = tab === "users";
              // Users: count of form links (rows). Files: count of distinct submitters.
              const badgeCount = isUsers ? g.rows.length : new Set(g.rows.map(r => r.email_id)).size;
              const badgeLabel = isUsers ? `${badgeCount} forms` : `${badgeCount} users`;
              const company = g.rows[0]?.company_name ?? "";
              // File cards always carry a department strip; fall back when the API omits the field.
              const department = g.rows[0]?.department?.trim() || "Uncategorized";
              const title = isUsers ? g.label : cleanFileName(g.label);
              // Files use a taller, richer art zone with a department strip; users a shorter one.
              const artHeight = isUsers ? 140 : 160;
              return (
                <Box
                  key={g.key}
                  onClick={() => setOpenGroup(g)}
                  sx={{
                    position: "relative",
                    bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: "14px",
                    cursor: "pointer", overflow: "hidden",
                    display: "flex", flexDirection: "column",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.45), 0 10px 28px -14px rgba(0,0,0,0.7)",
                    transition: "border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      borderColor: alpha(p.secondary.main, 0.32),
                      boxShadow: "0 12px 36px rgba(0,0,0,0.4)",
                    },
                  }}
                >
                  {/* Art zone: layered brand gradients behind the centred icon tile. */}
                  <Box
                    sx={{
                      position: "relative", height: artHeight, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      backgroundImage: isUsers
                        ? `radial-gradient(circle at 60% 40%, ${alpha(p.secondary.main, 0.22)}, transparent 70%), linear-gradient(160deg, #0D1728 0%, #0F1520 60%, #0C1630 100%)`
                        // Two offset blooms (brand blue + cyan) over a lighter navy base give the file art its richer, more colourful read.
                        : `radial-gradient(circle at 28% 62%, ${alpha(p.secondary.main, 0.3)}, transparent 62%), radial-gradient(circle at 82% 18%, rgba(63,190,210,0.22), transparent 55%), linear-gradient(145deg, #0B1626 0%, #10203A 55%, #0A1526 100%)`,
                      // Fade the art into the card body so the icon tile bleeds downward (mockup ::after).
                      "&::after": {
                        content: '""', position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1,
                        backgroundImage: `linear-gradient(to bottom, transparent 40%, ${p.background.paper} 100%)`,
                      },
                    }}
                  >
                    {/* count badge — top-right pill */}
                    <Box sx={{
                      position: "absolute", top: 10, right: 10, zIndex: 3,
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1, py: 0.4, borderRadius: "999px",
                      bgcolor: alpha(p.background.default, 0.65), backdropFilter: "blur(6px)",
                      border: "1px solid", borderColor: "divider", color: "text.secondary",
                    }}>
                      {isUsers
                        ? <AssignmentOutlinedIcon sx={{ fontSize: "0.7rem" }} />
                        : <PeopleAltOutlinedIcon sx={{ fontSize: "0.7rem" }} />}
                      <Typography sx={{ fontSize: "0.56rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{badgeLabel}</Typography>
                    </Box>

                    {/* centred icon tile */}
                    <Box sx={{
                      position: "relative", zIndex: 2,
                      width: 56, height: 56, borderRadius: "6px",
                      bgcolor: alpha(p.secondary.main, 0.12),
                      border: "1px solid", borderColor: alpha(p.secondary.main, 0.32),
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {isUsers
                        ? <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "secondary.main", letterSpacing: "0.04em" }}>{initialsFromEmail(g.key)}</Typography>
                        : <DescriptionOutlinedIcon sx={{ fontSize: "1.6rem", color: "secondary.main" }} />}
                    </Box>

                    {/* department strip (Files tab) — always shown, with a fallback label */}
                    {!isUsers && (
                      <Box sx={{
                        position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 2,
                        px: 1.75, pt: 1, pb: 1.25, display: "flex", alignItems: "center",
                        backgroundImage: `linear-gradient(to top, ${alpha(p.background.default, 0.85)} 0%, transparent 100%)`,
                      }}>
                        <Box sx={{
                          px: 1.1, py: 0.35, borderRadius: "999px",
                          bgcolor: alpha(p.secondary.main, 0.18), border: "1px solid", borderColor: alpha(p.secondary.main, 0.3),
                          backdropFilter: "blur(4px)",
                        }}>
                          <Typography sx={{ fontSize: "0.56rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "secondary.main" }}>
                            {department}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* body: title (2-line clamp) + meta — sized to the mockup (13px / 11px) */}
                  <Box sx={{ px: 2, pt: 1.25, pb: 2, display: "flex", flexDirection: "column", gap: 0.625 }}>
                    <Typography
                      title={title}
                      sx={{
                        fontWeight: 600, fontSize: "0.8125rem", lineHeight: 1.45, color: "text.primary",
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}
                    >
                      {title || (isUsers ? "(unknown user)" : "(unknown file)")}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: "0.6875rem", fontWeight: 500, color: "text.disabled" }}>
                      {isUsers ? (g.label || "—") : (company || "—")}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Group detail dialog — list of form links / submissions */}
      <Dialog
        open={!!openGroup}
        onClose={() => setOpenGroup(null)}
        // Panel uses the darkest surface; the item list below is elevated (lighter) to match the mockup's header/body split.
        PaperProps={{ sx: { width: "calc(100% - 48px)", maxWidth: 560, maxHeight: "70vh", m: 2, bgcolor: "background.default", backgroundImage: "none", border: "1px solid", borderColor: "divider", borderRadius: "10px", display: "flex", flexDirection: "column" } }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.75, px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: "1.06rem", letterSpacing: "-0.01em", color: "text.primary" }}>
              {tab === "users" ? openGroup?.label : cleanFileName(openGroup?.label ?? "")}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "text.disabled", mt: 0.4 }}>
              {tab === "users"
                ? `${openGroup?.rows.length ?? 0} form links`
                : `${new Set(openGroup?.rows.map(r => r.email_id) ?? []).size} users`}
            </Typography>
          </Box>
          {/* Bordered square close button (mockup .modal-close) rather than a round icon button. */}
          <IconButton
            onClick={() => setOpenGroup(null)}
            sx={{
              width: 28, height: 28, borderRadius: "4px", flexShrink: 0,
              border: "1px solid", borderColor: "divider", color: "text.secondary",
              "&:hover": { borderColor: "text.disabled", color: "text.primary", bgcolor: "transparent" },
            }}
          >
            <CloseIcon sx={{ fontSize: "0.95rem" }} />
          </IconButton>
        </Box>

        {/* Item list on an elevated surface so rows read as a distinct panel below the header (matches mockup). */}
        <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "background.paper", py: 1 }}>
          {openGroup?.rows.map((row, i) => {
            const isUsers = tab === "users";
            const hasFile = !!row.submission_url;
            // Users tab: row = a form link (icon + file name). Files tab: row = a submitter (avatar + email).
            const name = isUsers ? cleanFileName(row.file_name || "") : row.email_id;
            const sub = isUsers
              ? [row.company_name, row.department].filter(Boolean).join(" · ") || "—"
              : `${row.company_name || "—"} · ${formatSubmittedAt(row.submitted_at)}`;
            const secondaryDeleted = isUsers ? row.file_deleted : row.user_deleted;
            return (
              <Box
                key={row.submission_id}
                onClick={() => openPreview(row)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.75, px: 3, py: 1.5,
                  borderBottom: i === (openGroup?.rows.length ?? 0) - 1 ? "none" : "1px solid",
                  borderColor: "divider",
                  cursor: hasFile ? "pointer" : "default",
                  "&:hover": hasFile ? { bgcolor: "action.hover" } : undefined,
                }}
              >
                {isUsers ? (
                  <Box sx={{ width: 30, height: 30, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <AssignmentOutlinedIcon sx={{ fontSize: "1.15rem", color: hasFile ? "secondary.main" : "text.disabled" }} />
                  </Box>
                ) : (
                  <Box sx={{
                    width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                    background: "linear-gradient(135deg, #1778C7, #46A0EB)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.62rem", fontWeight: 700, color: "#fff",
                  }}>
                    {initialsFromEmail(row.email_id)}
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap title={name} sx={{ fontWeight: 600, fontSize: "0.8rem", color: hasFile ? "text.primary" : "text.disabled" }}>
                    {name || "(unknown)"}{!hasFile && " (no file)"}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: "0.7rem", fontWeight: 500, color: "text.disabled", mt: 0.25 }}>
                    {sub}
                  </Typography>
                </Box>
                {secondaryDeleted && (
                  <Box sx={{ px: 1, py: 0.3, borderRadius: "6px", bgcolor: alpha(p.error.main, 0.14), flexShrink: 0 }}>
                    <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "error.main" }}>
                      {isUsers ? "FILE DELETED" : "USER REMOVED"}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Dialog>

      {/* Inline submission preview (browser-native viewer) */}
      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        PaperProps={{ sx: { width: "90vw", height: "90vh", maxWidth: "none", m: 2, bgcolor: "background.paper", backgroundImage: "none", border: "1px solid", borderColor: "divider", borderRadius: "12px", display: "flex", flexDirection: "column" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2, pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography noWrap title={preview?.name} sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary" }}>
            {preview?.name} Preview
          </Typography>
          <IconButton size="small" onClick={() => setPreview(null)} sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {preview && (
            <Box component="iframe" src={preview.url} title={preview.name} sx={{ width: "100%", height: "100%", border: "none", display: "block", bgcolor: "#fff" }} />
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default ArchiveView;
